import i18n, { localeMap, type Language } from '@/i18n';

function currentLocale(): string {
  const lng = (i18n.resolvedLanguage ?? i18n.language ?? 'pt') as Language;
  return localeMap[lng] ?? 'pt-PT';
}

/** Formats a number as EUR currency in the active locale. */
export function formatEur(value: number): string {
  return new Intl.NumberFormat(currentLocale(), {
    style: 'currency',
    currency: 'EUR',
  }).format(Number.isFinite(value) ? value : 0);
}

/** yyyy-MM-dd → dd/MM (compact day/month for tables). */
export function formatDateShort(iso: string): string {
  const [, m, d] = iso.split('-');
  return d && m ? `${d}/${m}` : iso;
}

/** yyyy-MM-dd → localized full date. */
export function formatDateFull(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  return new Intl.DateTimeFormat(currentLocale(), { dateStyle: 'medium' }).format(new Date(y, m - 1, d));
}

/** yyyy-MM → localized "month year" label. */
export function formatMonthLabel(mes: string): string {
  const [y, m] = mes.split('-').map(Number);
  const date = new Date(y || 2000, (m || 1) - 1, 1);
  return new Intl.DateTimeFormat(currentLocale(), { month: 'long', year: 'numeric' }).format(date);
}

export function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function currentMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
}

export function shiftMonth(mes: string, delta: number): string {
  const [y, m] = mes.split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
}

/** dd/mm/aaaa (as read by OCR) → yyyy-MM-dd for a native date input. */
export function ptDateToIso(value: string): string {
  const match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec((value ?? '').trim());
  if (!match) return todayIso();
  return `${match[3]}-${pad(Number(match[2]))}-${pad(Number(match[1]))}`;
}

/**
 * Parses a decimal written in Portuguese/European or plain notation.
 * Rules: when both separators are present the LAST one is the decimal separator
 * (the other is a thousands separator); a lone comma is treated as the decimal
 * separator ("40,000" → 40); a lone dot is left as the decimal ("36.84" → 36.84,
 * matching OCR output).
 */
export function parseDecimal(value: string | number): number {
  if (typeof value === 'number') return value;
  let s = String(value).trim().replace(/\s/g, '');
  if (!s) return 0;

  const hasComma = s.includes(',');
  const hasDot = s.includes('.');

  if (hasComma && hasDot) {
    if (s.lastIndexOf(',') > s.lastIndexOf('.')) {
      s = s.replace(/\./g, '').replace(',', '.'); // comma is decimal
    } else {
      s = s.replace(/,/g, ''); // dot is decimal, commas are thousands
    }
  } else if (hasComma) {
    s = s.replace(',', '.'); // lone comma → decimal
  }

  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}
